/** @format */

import {
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	OnInit,
	signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompareGridComponent } from '@compare/components';
import { CompareBarProduct, CompareProductDetails, CompareService } from '@compare/data-access';
import { ProcessedNonBundleProduct } from '@shared/data-access/interfaces';
import { Scene7Service } from '@shared/data-access/services';
import {
	CertonaRecommendationsResponse,
	CertonaScheme,
	CertonaService
} from '@product-recommendations/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

@Component({
	selector: 'pdp-component-compare-section',
	imports: [CommonModule, CompareGridComponent],
	templateUrl: './compare-section.component.html'
})
export class CompareSectionComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly certonaService = inject(CertonaService);
	private readonly compareService = inject(CompareService);
	private readonly scene7Service = inject(Scene7Service);

	readonly currentProduct = input.required<ProcessedNonBundleProduct>();
	readonly currentProductSelectedColor = input.required<string>();
	readonly wrapperClasses = input<string>();

	readonly currentCompareBarProduct = computed<CompareBarProduct>(() => {
		const currentProductImage = this.scene7Service.getProductImageUrl(
			this.currentProduct().partNumber,
			'ProductBean',
			this.currentProductSelectedColor(),
			undefined,
			{ wid: '355', hei: '355', fmt: 'pjpeg' },
			false
		);

		return {
			imgURL: currentProductImage,
			ecode: this.currentProduct().partNumber,
			name: this.currentProduct().name,
			pdpURL: this.currentProduct().pdpSeoUrl,
			gridImageURL: currentProductImage,
			compareSource: 'currentProduct'
		};
	});

	private readonly certonaCompareBarProducts = signal<CompareBarProduct[]>([]);

	readonly compareBarProducts = computed(() => [
		this.currentCompareBarProduct(),
		...this.certonaCompareBarProducts()
	]);
	private readonly compareProductEcodes = computed(() =>
		this.compareBarProducts().map((product) => product.ecode)
	);

	readonly compareProductDetails = signal<CompareProductDetails | null>(null);
	readonly showCompare = computed(() => !!this.certonaCompareBarProducts().length);

	private readonly certonaMonetateScheme = 'product_rr';

	constructor() {
		// Update compare products when selected color changes or Certona/Monetate response comes in
		effect(() => {
			const ecodes = this.compareProductEcodes();

			// Only get ecode product details when there is more than 1 non-empty ecode (empty ecodes = monetate / certona loading)
			if (ecodes.filter((ecode) => !!ecode).length > 1) {
				this.compareService
					.getProductDetails(ecodes, false)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((productDetails) => {
						this.compareProductDetails.set(productDetails);
					});
			}
		});
	}

	ngOnInit() {
		this.certonaService.certonaRecommendationsState
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				// see product-recommendations.component.ts:241
				filter(
					(res) =>
						res?.certonaRecommendations?.resonance?.schemes?.find(
							(s: CertonaScheme) => s.scheme === this.certonaMonetateScheme
						) || res?.certonaRecommendations?.source === 'Monetate'
				)
			)
			.subscribe((response: CertonaRecommendationsResponse) =>
				this.handleCertonaMonetateResponse(response)
			);
	}

	private handleCertonaMonetateResponse(response: CertonaRecommendationsResponse) {
		if (!response?.certonaRecommendations?.resonance?.schemes) {
			return;
		}

		const schemas = response.certonaRecommendations.resonance.schemes.filter(
			(schema) => schema.scheme === this.certonaMonetateScheme
		);

		if (schemas.length === 0 && response.certonaRecommendations.source === 'Monetate') {
			// see product-recommendations.component.ts:420
			schemas.push({
				display: 'no',
				explanation: '',
				items: [],
				scheme: this.certonaMonetateScheme
			});
		}

		const newItems = schemas
			.map((schema) => schema.items)
			.flat()
			.slice(0, 4);

		if (!newItems.length) {
			this.certonaCompareBarProducts.set([]);
			return;
		}

		const newCompareBarProducts: CompareBarProduct[] = newItems.map((item) => {
			if (item) {
				return {
					ecode: item.ID,
					imgURL: `${item.ImageURL}&wid=355&hei=355&fmt=pjpeg`,
					gridImageURL: `${item.ImageURL}&wid=355&hei=355&fmt=pjpeg`,
					name: item.Name,
					pdpURL: item.URL
				};
			} else {
				// For skeleton loaders
				return {
					ecode: '',
					imgURL: '',
					gridImageURL: '',
					name: '',
					pdpURL: ''
				};
			}
		});

		this.certonaCompareBarProducts.set(newCompareBarProducts);
	}
}
